import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  useAvailabilityGrid,
  useShiftsGrid,
  useDeleteShift,
  type ShiftType,
  type ShiftStatus,
  type OperatorShift,
  type WeeklyAvailability,
} from "@/api/scheduling";
import { format, addDays } from "date-fns";
import { Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { AssignShiftDialog } from "./assign-shift-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface SchedulingGridProps {
  weekStart: Date;
  dispatchCenterId: string;
}

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;

const SHIFT_TYPES: ShiftType[] = ["MORNING", "AFTERNOON", "EVENING"];

const shiftConfig: Record<
  ShiftType,
  {
    label: string;
    short: string;
    time: string;
    bg: string;
    bgLight: string;
    text: string;
    border: string;
  }
> = {
  MORNING: {
    label: "Morning",
    short: "6-12",
    time: "6:00-12:00",
    bg: "bg-yellow-500",
    bgLight: "bg-yellow-50",
    text: "text-yellow-800",
    border: "border-yellow-300",
  },
  AFTERNOON: {
    label: "Afternoon",
    short: "12-18",
    time: "12:00-18:00",
    bg: "bg-blue-500",
    bgLight: "bg-blue-50",
    text: "text-blue-800",
    border: "border-blue-300",
  },
  EVENING: {
    label: "Evening",
    short: "18-24",
    time: "18:00-24:00",
    bg: "bg-purple-500",
    bgLight: "bg-purple-50",
    text: "text-purple-800",
    border: "border-purple-300",
  },
};

const statusColors: Record<ShiftStatus, string> = {
  SCHEDULED: "",
  CONFIRMED: "ring-2 ring-green-500",
  COMPLETED: "opacity-60",
  CANCELLED: "line-through opacity-40",
};

export function SchedulingGrid({
  weekStart,
  dispatchCenterId,
}: SchedulingGridProps) {
  const [dialogState, setDialogState] = useState<{
    open: boolean;
    operatorId: string;
    operatorName: string;
    date: Date;
    suggestedShift?: ShiftType;
  } | null>(null);

  const weekStartStr = format(weekStart, "yyyy-MM-dd");

  const { data: availabilityData, isLoading: availLoading } =
    useAvailabilityGrid({
      weekStart: weekStartStr,
      dispatchCenterId,
    });

  const { data: shiftsData, isLoading: shiftsLoading } = useShiftsGrid({
    weekStart: weekStartStr,
    dispatchCenterId,
  });

  const deleteShift = useDeleteShift({
    onSuccess: () => toast.success("Shift removed"),
    onError: () => toast.error("Failed to remove shift"),
  });

  const isLoading = availLoading || shiftsLoading;

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  // Merge availability and shifts data by operator
  const operatorIds = new Set([
    ...(availabilityData?.map((a) => a.operatorId) ?? []),
    ...(shiftsData?.map((s) => s.operatorId) ?? []),
  ]);

  const mergedData = Array.from(operatorIds).map((operatorId) => {
    const avail = availabilityData?.find((a) => a.operatorId === operatorId);
    const shifts = shiftsData?.find((s) => s.operatorId === operatorId);
    return {
      operatorId,
      name: avail?.name ?? shifts?.name ?? "Unknown",
      availability: avail?.availability ?? null,
      notes: avail?.notes ?? null,
      shifts: shifts?.shifts ?? [],
    };
  });

  if (mergedData.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No operators found for this dispatch center
      </div>
    );
  }

  const getShiftsForDay = (shifts: OperatorShift[], dayIndex: number) => {
    const targetDate = addDays(weekStart, dayIndex);
    const targetDateStr = format(targetDate, "yyyy-MM-dd");
    return shifts.filter((s) => s.date.startsWith(targetDateStr));
  };

  const getAvailabilityForDay = (
    availability: WeeklyAvailability | null,
    dayIndex: number
  ): ShiftType[] => {
    if (!availability) return [];
    const day = DAYS[dayIndex];
    return availability[day] ?? [];
  };

  return (
    <TooltipProvider>
      <div className="space-y-3">
        {/* Legend */}
        <div className="flex items-center gap-6 text-sm text-gray-600">
          <span className="font-medium">Legend:</span>
          <div className="flex items-center gap-4">
            {SHIFT_TYPES.map((type) => (
              <div key={type} className="flex items-center gap-1.5">
                <div className={`w-3 h-3 rounded ${shiftConfig[type].bg}`} />
                <span>{shiftConfig[type].label}</span>
                <span className="text-xs text-gray-400">
                  ({shiftConfig[type].time})
                </span>
              </div>
            ))}
          </div>
          <span className="text-gray-400">|</span>
          <div className="flex items-center gap-1.5">
            <div className="w-8 h-4 rounded border-2 border-dashed border-gray-300" />
            <span>Available</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-8 h-4 rounded bg-gray-800" />
            <span>Assigned</span>
          </div>
        </div>

        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-44 sticky left-0 bg-white z-10">
                  Operator
                </TableHead>
                {DAYS.map((day, index) => {
                  const date = addDays(weekStart, index);
                  const isToday =
                    format(date, "yyyy-MM-dd") ===
                    format(new Date(), "yyyy-MM-dd");
                  return (
                    <TableHead
                      key={day}
                      className={`text-center min-w-[120px] ${isToday ? "bg-blue-50" : ""}`}
                    >
                      <div className={isToday ? "font-bold text-blue-600" : ""}>
                        {format(date, "EEE")}
                      </div>
                      <div
                        className={`text-xs ${isToday ? "text-blue-600" : "text-gray-500"}`}
                      >
                        {format(date, "MMM d")}
                      </div>
                    </TableHead>
                  );
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
              {mergedData.map((item) => (
                <TableRow key={item.operatorId}>
                  <TableCell className="font-medium sticky left-0 bg-white z-10">
                    <div>{item.name}</div>
                    {item.notes && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="text-xs text-gray-400 truncate max-w-[140px] cursor-help">
                            📝 {item.notes}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-xs">{item.notes}</p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </TableCell>
                  {DAYS.map((day, dayIndex) => {
                    const date = addDays(weekStart, dayIndex);
                    const isToday =
                      format(date, "yyyy-MM-dd") ===
                      format(new Date(), "yyyy-MM-dd");
                    const dayShifts = getShiftsForDay(item.shifts, dayIndex);
                    const dayAvailability = getAvailabilityForDay(
                      item.availability,
                      dayIndex
                    );

                    return (
                      <TableCell
                        key={day}
                        className={`p-1.5 ${isToday ? "bg-blue-50" : ""}`}
                      >
                        <div className="flex flex-col gap-1">
                          {SHIFT_TYPES.map((shiftType) => {
                            const isAvailable =
                              dayAvailability.includes(shiftType);
                            const assignedShift = dayShifts.find(
                              (s) => s.shiftType === shiftType
                            );
                            const config = shiftConfig[shiftType];

                            if (assignedShift) {
                              // Show assigned shift
                              return (
                                <div
                                  key={shiftType}
                                  className={`flex items-center justify-between px-2 py-1 rounded text-white text-xs ${config.bg} ${statusColors[assignedShift.status]}`}
                                >
                                  <span className="font-medium">
                                    {config.short}
                                  </span>
                                  {assignedShift.startTime && (
                                    <span className="opacity-80 text-[10px]">
                                      {assignedShift.startTime}
                                    </span>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() =>
                                      deleteShift.mutate(assignedShift.id)
                                    }
                                    className="ml-1 opacity-70 hover:opacity-100"
                                    disabled={deleteShift.isPending}
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                </div>
                              );
                            }

                            if (isAvailable) {
                              // Show available slot (clickable to assign)
                              return (
                                <button
                                  type="button"
                                  key={shiftType}
                                  onClick={() =>
                                    setDialogState({
                                      open: true,
                                      operatorId: item.operatorId,
                                      operatorName: item.name,
                                      date,
                                      suggestedShift: shiftType,
                                    })
                                  }
                                  className={`flex items-center justify-center px-2 py-1 rounded border-2 border-dashed ${config.border} ${config.bgLight} ${config.text} text-xs hover:border-solid hover:${config.bg} transition-all`}
                                >
                                  <Plus className="h-3 w-3 mr-1" />
                                  <span>{config.short}</span>
                                </button>
                              );
                            }

                            // Empty - no availability, no shift
                            return null;
                          })}

                          {/* If no availability and no shifts, show add button */}
                          {dayAvailability.length === 0 &&
                            dayShifts.length === 0 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 text-gray-400 hover:text-gray-600"
                                onClick={() =>
                                  setDialogState({
                                    open: true,
                                    operatorId: item.operatorId,
                                    operatorName: item.name,
                                    date,
                                  })
                                }
                              >
                                <Plus className="h-3 w-3 mr-1" />
                                Add
                              </Button>
                            )}
                        </div>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {dialogState && (
        <AssignShiftDialog
          open={dialogState.open}
          onOpenChange={(open) => !open && setDialogState(null)}
          operatorId={dialogState.operatorId}
          operatorName={dialogState.operatorName}
          dispatchCenterId={dispatchCenterId}
          date={dialogState.date}
          suggestedShift={dialogState.suggestedShift}
        />
      )}
    </TooltipProvider>
  );
}
